import type { DockviewApi, SerializedDockview } from 'dockview-react';

const EDITOR_ANCHOR = 'editor';

/**
 * Drop document tabs from a serialized dockview tree so a reload does not
 * bring the previous partition's documents back. Split sizes stay. Auxiliary
 * tabs stay: only the group that holds the editor anchor is stripped, and
 * only panels whose id is an instance (`viewId::instanceKey`).
 */
export function stripEditorInstanceTabs(
  serialized: SerializedDockview,
): SerializedDockview {
  const copy = JSON.parse(JSON.stringify(serialized)) as SerializedDockview;
  const drop = new Set<string>();
  const groups: { views: string[]; activeView?: string }[] = [];
  collectGroups(copy, groups);
  for (const group of groups) {
    if (!group.views.includes(EDITOR_ANCHOR)) {
      continue;
    }
    const kept = group.views.filter(
      (id) => id === EDITOR_ANCHOR || !id.includes('::'),
    );
    for (const id of group.views) {
      if (!kept.includes(id)) {
        drop.add(id);
      }
    }
    group.views.splice(0, group.views.length, ...kept);
    if (group.activeView && drop.has(group.activeView)) {
      group.activeView = EDITOR_ANCHOR;
    }
  }
  for (const group of groups) {
    const kept = group.views.filter((id) => !drop.has(id));
    if (kept.length !== group.views.length) {
      group.views.splice(0, group.views.length, ...kept);
      if (group.activeView && drop.has(group.activeView)) {
        group.activeView = kept[0];
      }
    }
  }
  const panels = copy.panels as Record<string, unknown> | undefined;
  if (panels) {
    for (const id of drop) {
      delete panels[id];
    }
  }
  return copy;
}

/** Remove live editor-group instance tabs after a layout restore. */
export function dropEditorInstancePanels(api: DockviewApi): void {
  const anchor = api.getPanel(EDITOR_ANCHOR);
  if (!anchor) {
    return;
  }
  for (const panel of [...anchor.group.panels]) {
    if (panel.id !== EDITOR_ANCHOR) {
      api.removePanel(panel);
    }
  }
}

function collectGroups(
  node: unknown,
  groups: { views: string[]; activeView?: string }[],
): void {
  if (!node || typeof node !== 'object') {
    return;
  }
  if (Array.isArray(node)) {
    for (const item of node) {
      collectGroups(item, groups);
    }
    return;
  }
  const record = node as { views?: unknown; activeView?: unknown };
  if (
    Array.isArray(record.views) &&
    record.views.every((id) => typeof id === 'string')
  ) {
    groups.push(record as { views: string[]; activeView?: string });
  }
  for (const value of Object.values(record)) {
    if (value && typeof value === 'object') {
      collectGroups(value, groups);
    }
  }
}
